import { useEffect, useRef, useState } from 'react'

import {
  Box,
  Button,
  Card,
  CollapsableCard,
  Colors,
  Icon,
  RadioButton,
  Text,
} from '@factorialco/deprecated-design-system'
import { FormikProvider } from 'formik'
import {
  CompetenciesCompetencyCrumbDocument,
  CompetenciesCompetencyLevelsAssignSidepanelBulkAssignDocument,
  CompetenciesCompetencyLevelsAssignSidepanelCompetencyFragment,
  CompetenciesCompetencyLevelsAssignSidepanelCreateDocument,
  CompetenciesCompetencyLevelsAssignSidepanelDocument,
  CompetencyLevelFragment,
  CompetencyRoleLevelsDataCollectionDocument,
  NodeCompetenciesDataCollectionDocument,
  NodeCompetenciesPageDocument,
} from 'generated/resources/graphql'
import { v4 as uuid } from 'uuid'

import Loading from 'modules/core/components/Loading'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm, useFormContext } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  LevelForm,
  normalizeLevelName,
  validateLevel,
} from 'modules/competencies/components/CompetencyLevelForm'
import { AddLevelButton } from 'modules/competencies/components/CompetencyLevelsForm'

interface JobCatalogNode {
  uuid: string
  name: string
  ancestorName?: string
}

export interface JobCatalogNodeAssignment {
  node: JobCatalogNode
  competencyLevelId?: string
}

interface CompetencyLevelCardProps {
  level: CompetencyLevelFragment
  selected: boolean
  onSelect: () => void
}

const CompetencyLevelCard = ({ level, selected, onSelect }: CompetencyLevelCardProps) => {
  return (
    <Card key={level.id} onClick={onSelect}>
      <Card.Title
        size="small"
        action={
          <RadioButton
            size="medium"
            value={level.id.toString()}
            checked={selected}
            aria-label={level.name}
            aria-checked={selected}
            onChange={onSelect}
          />
        }
      >
        {level.name}
      </Card.Title>
      <Card.Description>{level.description}</Card.Description>
    </Card>
  )
}

interface NodeAssignmentCollapsableCardProps {
  node: JobCatalogNodeAssignment['node']
  selectedLevel?: CompetencyLevelFragment
  levels: CompetencyLevelFragment[]
  canAddLevel: boolean
  isOpen: boolean
  isFormOpen: boolean
  onToggle: () => void
  onToggleForm: () => void
  onSelectLevel: (node: JobCatalogNodeAssignment['node'], competencyLevelId: string) => void
}

const NodeAssignmentCollapsableCard = ({
  node,
  selectedLevel,
  levels,
  canAddLevel,
  isOpen,
  isFormOpen,
  onToggle,
  onToggleForm,
  onSelectLevel,
}: NodeAssignmentCollapsableCardProps) => {
  return (
    <CollapsableCard
      open={isOpen}
      contentPadding="s24"
      contentBackground="grey200"
      Content={
        <Box gap="s8">
          {levels.map((level) => (
            <CompetencyLevelCard
              key={level.id}
              level={level}
              selected={selectedLevel?.id === level.id}
              onSelect={() => onSelectLevel(node, level.id)}
            />
          ))}
          {canAddLevel && (
            <CreateLevelForm
              isFormOpen={isFormOpen}
              onToggleForm={onToggleForm}
              backgroundColor="grey000White"
            />
          )}
        </Box>
      }
      Card={
        <Box
          flexDirection="row"
          justifyContent="spaceBetween"
          alignItems="center"
          onClick={onToggle}
          style={{ cursor: 'pointer' }}
        >
          <Box gap="s4" flexDirection="row" alignItems="center">
            <Icon.Medium
              icon="check-circle"
              color={selectedLevel ? 'viridian1000' : 'iconSecondary'}
            />
            <Text.Normal size="300" weight="medium" color="grey800">
              {[node?.ancestorName, node.name].join(' ')} {selectedLevel ? '/' : ''}
            </Text.Normal>
            {selectedLevel && (
              <Text.Normal size="300" weight="semibold" color="textPrimary">
                {selectedLevel.name}
              </Text.Normal>
            )}
          </Box>

          <Icon.Small icon={isOpen ? 'chevron-up' : 'chevron-down'} color="iconPrimary" />
        </Box>
      }
    />
  )
}

interface CompetencyHeaderProps {
  competency: CompetenciesCompetencyLevelsAssignSidepanelCompetencyFragment
  isAssigningToMultipleNodes: boolean
}

const CompetencyHeader = ({ competency, isAssigningToMultipleNodes }: CompetencyHeaderProps) => {
  const competencyLevels = competency?.competencyLevels ?? []

  return (
    <>
      <Box gap="s4">
        <Text.Normal size="400" weight="semibold" color="textPrimary">
          {competency.name}
        </Text.Normal>
        {competency.description && (
          <Text.Normal size="300" color="textSecondary">
            {competency.description}
          </Text.Normal>
        )}
      </Box>

      <Text.Normal size="300" weight="semibold" color="textPrimary">
        {competencyLevels.length > 0
          ? isAssigningToMultipleNodes
            ? i18n.t('competencies_v3.levels.assign.select_multiple')
            : i18n.t('competencies_v3.levels.assign.select_one')
          : i18n.t('competencies_v3.levels.assign.no_competency_levels')}
      </Text.Normal>
    </>
  )
}

interface CreateLevelFormProps {
  isFormOpen: boolean
  onToggleForm: () => void
  backgroundColor?: Colors
}

const CreateLevelForm = ({ isFormOpen, onToggleForm, backgroundColor }: CreateLevelFormProps) => {
  const form = useFormContext<CreateLevelFormValues>()

  return (
    <>
      {isFormOpen && (
        <Box
          flexDirection="column"
          gap="s16"
          background={backgroundColor ?? 'grey200'}
          border={
            backgroundColor === 'grey000White'
              ? { all: { color: 'grey400', width: 's1', style: 'solid' } }
              : undefined
          }
          borderRadius={{ all: 'abs016' }}
          padding="s24"
        >
          <LevelForm
            level={{ ...form.values, id: uuid() }}
            errors={form.errors}
            touched={form.touched}
            onSave={async () => form.submitForm()}
            onDiscard={() => {
              form.resetForm()
              onToggleForm()
            }}
            onChange={({ name, description }) => form.setValues({ name, description })}
            onBlur={(fieldKey) => form.setFieldTouched(fieldKey, true)}
          />
        </Box>
      )}

      {!isFormOpen && (
        <Box width="min-content">
          <AddLevelButton onClick={onToggleForm} />
        </Box>
      )}
    </>
  )
}

interface CreateLevelFormValues {
  name: string
  description: string
}

interface Props {
  open: boolean
  competencyId: string
  jobCatalogNodeAssignments: JobCatalogNodeAssignment[]
  title?: string
  renderHeader?: (props: {
    competency: CompetenciesCompetencyLevelsAssignSidepanelCompetencyFragment
    isAssigningToMultipleNodes: boolean
  }) => React.ReactNode
  onClose: () => void
}

export const CompetencyLevelsAssignSidepanel = ({
  open,
  competencyId,
  jobCatalogNodeAssignments,
  title = i18n.tp('competencies_v3.levels.assign.title_assign_expected', {
    count: jobCatalogNodeAssignments.length,
  }),
  renderHeader = (props) => <CompetencyHeader {...props} />,
  onClose,
}: Props) => {
  const { company } = useCurrentSession()
  const [assignments, setAssignments] = useState<JobCatalogNodeAssignment[]>([])
  const [openAssignmentCard, setOpenAssignmentCard] = useState<JobCatalogNodeAssignment | null>(
    null
  )

  const [openLevelForm, setOpenLevelForm] = useState(false)
  const previousOpenRef = useRef(false)

  const { data, loading } = useQuery(CompetenciesCompetencyLevelsAssignSidepanelDocument, {
    variables: { competencyId },
    skip: !open,
  })

  const [createLevel] = useMutation(CompetenciesCompetencyLevelsAssignSidepanelCreateDocument, {
    extractorFn: ({ competenciesMutations }) =>
      competenciesMutations.appendCompetenciesCompetencyLevel,
  })

  const [assignLevels, { loading: assigningLevels }] = useMutation(
    CompetenciesCompetencyLevelsAssignSidepanelBulkAssignDocument,
    {
      extractorFn: ({ competenciesMutations }) =>
        competenciesMutations.bulkAssignToJobCatalogTreeCompetenciesCompetency,
    }
  )

  const competency = data?.competencies.competency
  const competencyLevels = competency?.competencyLevels ?? []

  const canAddLevel = data?.competencies._policies.competenciesManage.can ?? false
  const firstAssignment = assignments[0]

  const isAssigningToMultipleNodes = assignments.length > 1

  const form = useForm<CreateLevelFormValues>({
    defaultValues: {
      name: '',
      description: '',
    },
    validate: (values) => {
      const existingNames = competencyLevels.map(({ name }) => normalizeLevelName(name))
      const { errors } = validateLevel(
        values,
        existingNames.includes(normalizeLevelName(values.name))
      )
      return errors
    },
    onSubmit: async (values) => {
      const result = await createLevel({
        variables: { competencyId, ...values },
        refetchQueries: [
          CompetenciesCompetencyLevelsAssignSidepanelDocument,
          CompetenciesCompetencyCrumbDocument,
        ],
        awaitRefetchQueries: true,
      })

      const handled = handleResult(result, (errors) => form.setErrors(errors))

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('competencies_v3.levels.assign.level_created_successfully'),
        })
        form.resetForm()
        setOpenLevelForm(false)
      }

      return handled
    },
  })

  const handleAssign = async () => {
    const mappedAssignments = assignments
      .filter((assignment) => assignment.competencyLevelId)
      .map(({ node, competencyLevelId }) => ({
        competencyId,
        competencyLevelId,
        nodeUuid: node.uuid,
      }))

    const result = await assignLevels({
      variables: {
        companyId: company.id,
        assignments: mappedAssignments,
      },
      refetchQueries: [
        NodeCompetenciesDataCollectionDocument,
        NodeCompetenciesPageDocument,
        CompetencyRoleLevelsDataCollectionDocument,
      ],
      awaitRefetchQueries: true,
    })

    const handled = handleResult(result, () => null)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.tp('competencies_v3.levels.assign.levels_assigned_successfully', {
          count: mappedAssignments.length,
        }),
      })
      handleClose()
    }

    return handled
  }

  const handleSelectLevel = (node: JobCatalogNodeAssignment['node'], competencyLevelId: string) => {
    setAssignments((prev) => prev.map((a) => (a.node === node ? { node, competencyLevelId } : a)))

    const assignmentMissingCompetencyLevel = assignments.find(
      (a) => !a.competencyLevelId && a.node !== node
    )
    setOpenAssignmentCard(assignmentMissingCompetencyLevel ?? null)
  }

  const findCompetencyLevel = (competencyLevelId?: string) => {
    if (!competencyLevelId) return
    return competencyLevels.find((level) => level.id === competencyLevelId)
  }

  const handleClose = () => {
    form.resetForm()
    setOpenLevelForm(false)
    onClose()
  }

  useEffect(() => {
    // Only initialize when opening the sidepanel
    if (!previousOpenRef.current && open) {
      setAssignments(jobCatalogNodeAssignments)

      const assignmentMissingCompetencyLevel = jobCatalogNodeAssignments.find(
        ({ competencyLevelId }) => !competencyLevelId
      )
      setOpenAssignmentCard(assignmentMissingCompetencyLevel ?? null)
    }

    previousOpenRef.current = open
  }, [open, jobCatalogNodeAssignments])

  return (
    <Sidepanel
      open={open}
      onClose={handleClose}
      width="wideXL"
      title={title}
      footer={
        <>
          <Button onClick={handleClose} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button
            onClick={handleAssign}
            loading={
              form.isSubmitting ||
              loading ||
              assigningLevels ||
              assignments.every((assignment) => !assignment.competencyLevelId)
            }
          >
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      {loading || !competency ? (
        <Loading expand />
      ) : (
        <FormikProvider value={form}>
          <Box gap="s24">
            {renderHeader({ competency, isAssigningToMultipleNodes })}

            {competencyLevels.length > 0 && (
              <>
                {isAssigningToMultipleNodes
                  ? assignments.map((assignment) => (
                      <NodeAssignmentCollapsableCard
                        key={assignment.node.uuid}
                        node={assignment.node}
                        selectedLevel={findCompetencyLevel(assignment.competencyLevelId)}
                        levels={competencyLevels}
                        canAddLevel={canAddLevel}
                        isOpen={openAssignmentCard === assignment}
                        isFormOpen={openLevelForm}
                        onToggleForm={() => setOpenLevelForm((prev) => !prev)}
                        onToggle={() => {
                          setOpenAssignmentCard(
                            openAssignmentCard === assignment ? null : assignment
                          )
                          form.resetForm()
                          setOpenLevelForm(false)
                        }}
                        onSelectLevel={handleSelectLevel}
                      />
                    ))
                  : competencyLevels.map((level) => (
                      <CompetencyLevelCard
                        key={level.id}
                        level={level}
                        selected={firstAssignment?.competencyLevelId === level.id}
                        onSelect={() =>
                          firstAssignment && handleSelectLevel(firstAssignment.node, level.id)
                        }
                      />
                    ))}
              </>
            )}

            {canAddLevel && (!isAssigningToMultipleNodes || competencyLevels.length === 0) && (
              <CreateLevelForm
                isFormOpen={openLevelForm}
                onToggleForm={() => setOpenLevelForm((prev) => !prev)}
              />
            )}
          </Box>
        </FormikProvider>
      )}
    </Sidepanel>
  )
}
